import { Box, Button, Checkbox, Group, Loader, Stack, Text } from '@mantine/core';
import { useState } from 'react';

import { CustomsError, SortDirection } from '@e-origin/shared';

import { useAdminCustomsErrors, useAdminExportCustomsError } from '@admin/hooks';
import { Table, TableColumnProps } from '@components';

interface ExportCustomsErrorsModalProps {
  onClose: () => void;
}

export const ExportCustomsErrorsModal: React.FC<ExportCustomsErrorsModalProps> = ({ onClose }) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState({ page: 1, size: 10 });

  const { data: { data: { customsErrors = [], totalItems = 0 } = {} } = {} } = useAdminCustomsErrors({
    pagination,
    filters: {},
    sorting: { field: 'createdAt', direction: SortDirection.DESC },
  });

  const exportMutation = useAdminExportCustomsError();

  const handleSelectAllPage = () => {
    const pageIds = customsErrors.map((err) => err._id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleDeselectAllPage = () => {
    const pageIds = customsErrors.map((err) => err._id);
    setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
  };

  const columns: TableColumnProps<CustomsError>[] = [
    {
      name: '',
      cell: (row) => (
        <Checkbox
          checked={selectedIds.includes(row._id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedIds((prev) => [...prev, row._id]);
            } else {
              setSelectedIds((prev) => prev.filter((id) => id !== row._id));
            }
          }}
        />
      ),
      width: 40,
    },
    {
      name: 'Error Message',
      cell: (row) => row.errorMessage,
    },
    {
      name: 'Error Reason',
      cell: (row) => row.errorReason,
    },
  ];

  const handleExport = () => {
    exportMutation.mutate(selectedIds);
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  return (
    <Stack gap={0}>
      <Group justify="start" mb="xs">
        <Button variant="filled" size="xs" onClick={handleSelectAllPage}>
          Select All (Page)
        </Button>
        <Button variant="filled" size="xs" onClick={handleDeselectAllPage}>
          Deselect All (Page)
        </Button>
      </Group>
      <Box h={550}>
        <Table columns={columns} dataSource={customsErrors as any} total={totalItems} onPaginationChange={handlePagination} verticalSpacing="xs" />
      </Box>
      {exportMutation.isError && <Text c="red">{exportMutation.error?.message || 'Export failed'}</Text>}
      <Group justify="center" mt="md">
        <Button variant="default" onClick={onClose} disabled={exportMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="filled"
          onClick={handleExport}
          disabled={selectedIds.length === 0 || exportMutation.isPending}
          leftSection={exportMutation.isPending && <Loader color="white" size="xs" />}
        >
          Export Selected
        </Button>
      </Group>
    </Stack>
  );
};
